import { Container, Box, Header, Grid, Icon } from '@cloudscape-design/components';

import { ViewLayout } from '../../../components/ViewLayout';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import {
  GOOGLE_PIPELINE_TASKS,
  LoadedPlaceReportPublic,
  PlaceReportPublic,
  PlaceSourceSnapshot,
  Task,
} from '../../../common/types';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReportPublic } from '../../../common/api/reports';
import { LoadingOrError } from '../../../components/Loading';
import ReportCardHeader from '../../../components/ReportCardHeader';
import { useQueryParam, withDefault, StringParam } from 'use-query-params';
import { ScorecardHeader } from '../../../components/ScorecardHeader';
import {
  ScorecardLoadingView,
  ScorecardLoadingReport,
} from '../../../components/ScorecardLoadingView';
import { SourceRatingsSummary } from '../../../components/SourceRatingsSummary';
import { periodMatchesSource } from '../../../common/cardLogic';
import { validateCustomerVolumeInput } from '../../../common/customerVolume';
import {
  CALENDAR_PERIOD_OPTIONS,
  CalendarPeriodControl,
} from '../../../components/CalendarPeriodControl';

export default function View() {
  const { publicCode } = useParams({ from: '/create/$publicCode' });
  const { completion, delivery, customer_volume } = useSearch({ from: '/create/$publicCode' });
  const customerVolume = validateCustomerVolumeInput(customer_volume).customerVolume;

  const getQuery = useQuery({
    queryKey: ['reports', publicCode, customerVolume],
    queryFn: () => getReportPublic(publicCode ?? '', customerVolume),
    enabled: !!publicCode,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return (
    <ViewLayout
      header={<ReportCardHeader report={getQuery.data ?? undefined} />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      {getQuery.isLoading || !getQuery.data || getQuery.error || !publicCode ? (
        <LoadingOrError
          loading={getQuery.isLoading}
          retry={() => getQuery.refetch()}
          error={getQuery.error}
        />
      ) : (
        <CardTaskView
          report={getQuery.data}
          publicCode={publicCode}
          redirectToScorecard={completion === 'scorecard'}
          delivery={delivery}
          customerVolume={customerVolume}
        />
      )}
    </ViewLayout>
  );
}

function sortTasks(a: Task, b: Task) {
  const aIndex = GOOGLE_PIPELINE_TASKS.indexOf(a.name);
  const bIndex = GOOGLE_PIPELINE_TASKS.indexOf(b.name);
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  // if the index is the same, sort by min progress
  const aProgress = a.progress ?? 0;
  const bProgress = b.progress ?? 0;
  if (aProgress !== bProgress) {
    return aProgress - bProgress;
  }
  // if the progress is the same, sort by created_at
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
}

const REQUIRED_GOOGLE_RANGES = ['30-days', '60-days', '90-days', '180-days'] as const;

export type CardTaskViewProps = {
  report: PlaceReportPublic;
  publicCode: string;
  redirectToScorecard?: boolean;
  delivery?: 'text-email' | 'text' | 'email';
  customerVolume?: number;
};

export function CardTaskView(props: CardTaskViewProps) {
  const navigate = useNavigate();
  const [report, setReport] = useState(props.report);
  const getReq = useMutation({
    mutationFn: () => getReportPublic(props.publicCode, props.customerVolume),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (data && JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  const currentTask = useMemo(() => {
    if (report.tasks.length === 0) {
      return undefined;
    }
    // Only consider Google pipeline tasks for progress tracking
    // This excludes Yelp/BBB/DealerRater tasks that run in parallel
    const filteredTasks = [...report.tasks]
      .filter((task) => GOOGLE_PIPELINE_TASKS.includes(task.name) && task.status !== 'completed')
      .sort(sortTasks);
    return filteredTasks[0];
  }, [report.tasks]);

  const hasCompleteGooglePeriods = useMemo(() => {
    return REQUIRED_GOOGLE_RANGES.every((range) =>
      report.periods?.some(
        (period) => period.range === range && periodMatchesSource(period, 'google'),
      ),
    );
  }, [report.periods]);

  const status = useMemo(() => {
    if (hasCompleteGooglePeriods) {
      return 'success';
    }

    // Only show error when ALL tasks are terminal (completed or permanently failed)
    // and no Google periods exist. We can't check individual task failures because
    // collect_reviews_source/report_statistics_source are shared names across all
    // sources (Google, Yelp, BBB, etc.) - a non-Google task failing doesn't mean
    // the Google pipeline has failed.
    const allTasksTerminal =
      report.tasks.length > 0 &&
      report.tasks.every(
        (t) =>
          t.status === 'completed' ||
          (t.status === 'failed' && t.retries != null && t.retries >= 3),
      );
    if (allTasksTerminal) {
      return 'error';
    }

    // Otherwise, still in progress - waiting for Google statistics to complete
    return 'in-progress';
  }, [report.tasks, hasCompleteGooglePeriods]);

  useEffect(() => {
    if (props.redirectToScorecard && status === 'success' && report.slug) {
      navigate({ to: `/${report.slug}${window.location.search}`, replace: true });
    }
  }, [navigate, props.redirectToScorecard, report.slug, status]);

  const pollReport = useEffectEvent(() => {
    if (document.hidden || navigator.onLine === false || getReq.isPending) {
      return;
    }

    void getReq.mutateAsync().catch(() => undefined);
  });

  useEffect(() => {
    if (status === 'success') {
      return;
    }

    // Poll more frequently (2s) to get progress updates during review collection.
    const pollInterval = setInterval(pollReport, 2000);

    return () => clearInterval(pollInterval);
  }, [status]);

  if (status === 'success' && report.place !== undefined) {
    if (props.redirectToScorecard) {
      return <LoadingOrError loading />;
    }
    // TODO show message to check email or text for report card
    return (
      <CompletedCardView report={report as LoadedPlaceReportPublic} delivery={props.delivery} />
    );
  }

  return (
    <ScorecardLoadingView
      report={report as ScorecardLoadingReport}
      currentTask={currentTask}
      status={status}
    />
  );
}

export type CompletedCardViewProps = {
  report: LoadedPlaceReportPublic;
  delivery?: 'text-email' | 'text' | 'email';
};

const RangeFiltersParam = withDefault(StringParam, '180-days');
const SourceFiltersParam = withDefault(StringParam, 'google');

export function CompletedCardView(props: CompletedCardViewProps) {
  const report = props.report;
  const deliveryMessage =
    props.delivery === 'text-email'
      ? 'Your scorecard was sent by text and email.'
      : props.delivery === 'text'
        ? 'Your scorecard was sent by text.'
        : props.delivery === 'email'
          ? 'Your scorecard was sent by email.'
          : 'Your scorecard is ready.';

  // Build snapshot map for O(1) lookups by source (same pattern as CardView)
  const snapshotMap = useMemo(() => {
    const map = new Map<string, PlaceSourceSnapshot>();
    for (const s of report.source_snapshots ?? []) {
      map.set(s.source, s);
    }
    return map;
  }, [report.source_snapshots]);

  const hasSourceSnapshot = (source: string) => !!snapshotMap.get(source)?.link;

  // Show automotive sources if place has the respective links or snapshots
  const hasDealerRater = hasSourceSnapshot('dealerrater') || !!report.place?.dealerrater_link;
  const hasCarsCom = hasSourceSnapshot('carscom') || !!report.place?.carscom_link;
  const hasCarFax = hasSourceSnapshot('carfax') || !!report.place?.carfax_link;
  const hasEdmunds = hasSourceSnapshot('edmunds') || !!report.place?.edmunds_link;
  const hasCarGurus = hasSourceSnapshot('cargurus') || !!report.place?.cargurus_link;

  // Show restaurant/hotel/golf sources
  const hasOpenTable = hasSourceSnapshot('opentable') || !!report.place?.opentable_link;
  const hasTripAdvisor = hasSourceSnapshot('tripadvisor') || !!report.place?.tripadvisor_link;
  const hasBooking = hasSourceSnapshot('booking') || !!report.place?.booking_link;
  const hasExpedia = hasSourceSnapshot('expedia') || !!report.place?.expedia_link;
  const hasGolfPass = hasSourceSnapshot('golfpass') || !!report.place?.golfpass_link;

  // TODO change to use report.report_at?
  const createdAt = new Date(report.report_at + 'Z');
  const rangeEnd = createdAt.toLocaleDateString();

  const [selectedRange, setSelectedRange] = useQueryParam('range', RangeFiltersParam);
  const [selectedSource, setSelectedSource] = useQueryParam('source', SourceFiltersParam);
  const rangeOptions = CALENDAR_PERIOD_OPTIONS;

  const currentRangeIdx = useMemo(() => {
    return report.periods.findIndex(
      (period) => period.range === selectedRange && period.source === selectedSource,
    );
  }, [report.periods, selectedRange, selectedSource]);

  const period = report.periods[currentRangeIdx];
  const placeLetterGrade = period?.place_letter_grade ?? '';
  const placePercentile = period?.place_percentile ?? 0;
  const borderColor = useMemo(() => {
    const grade = period?.place_letter_grade ?? '';
    if (grade.includes('A')) {
      return 'border-green-600';
    } else if (grade.includes('B')) {
      return 'border-green-400';
    } else if (grade.includes('C')) {
      return 'border-yellow-400';
    }
    // D and F are both red
    return 'border-red-500';
  }, [period?.place_letter_grade]);

  if (!period) {
    return <LoadingOrError loading />;
  }

  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
          },
        },
        {
          colspan: {
            default: 12,
          },
        },
      ]}
    >
      <Container
        header={
          <Header variant="h1" description={`Created ${rangeEnd}`}>
            Reputation Scorecard
          </Header>
        }
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-stretch gap-4">
          <div className="flex min-w-0 flex-col order-1 md:flex-1">
            <ScorecardHeader
              placeLetterGrade={placeLetterGrade}
              placePercentile={placePercentile}
              place={report.place}
              snapshotMap={snapshotMap}
              borderColor={borderColor}
              source={selectedSource}
            />
            <div className="hidden md:block mt-auto pt-2">
              <CalendarPeriodControl
                selectedId={selectedRange}
                onChange={setSelectedRange}
                options={rangeOptions}
              />
            </div>
          </div>
          <div className="order-2 flex min-w-0 flex-col gap-4 md:order-3 md:w-[23rem] md:shrink-0 xl:w-[28rem]">
            <SourceRatingsSummary
              place={report.place}
              snapshotMap={snapshotMap}
              showDealerRater={hasDealerRater}
              showCarsCom={hasCarsCom}
              showCarFax={hasCarFax}
              showEdmunds={hasEdmunds}
              showCarGurus={hasCarGurus}
              showOpenTable={hasOpenTable}
              showTripAdvisor={hasTripAdvisor}
              showBooking={hasBooking}
              showExpedia={hasExpedia}
              showGolfPass={hasGolfPass}
            />
          </div>
          <div className="order-3 md:hidden">
            <CalendarPeriodControl
              selectedId={selectedRange}
              onChange={setSelectedRange}
              options={rangeOptions}
            />
          </div>
        </div>
      </Container>
      <Box variant="div" textAlign="center" className="p-4">
        <Box variant="p" textAlign="center" fontSize="heading-l" color="text-status-info">
          <Icon name="status-info" variant="subtle" size="inherit" /> {deliveryMessage}
        </Box>
      </Box>
    </Grid>
  );
}
